import api from "../../../api/axios";

const TASK_LIST_PATH = "/api/crm/tasks/";
const TASKS_API_RELATIVE = "/crm/tasks/";
const MAX_PAGES = 100;

function getLocationContext() {
  if (typeof window !== "undefined") {
    return {
      origin: window.location.origin,
      hostname: window.location.hostname,
      protocol: window.location.protocol,
    };
  }
  return {
    origin: "http://[REDACTED]", // pragma: allowlist secret
    hostname: "[REDACTED]", // pragma: allowlist secret
    protocol: "http:",
  };
}

function paramsFromSearchParams(searchParams) {
  const params = {};
  for (const [key, value] of searchParams.entries()) {
    if (params[key] == null) {
      params[key] = value;
    } else if (Array.isArray(params[key])) {
      params[key].push(value);
    } else {
      params[key] = [params[key], value];
    }
  }
  return params;
}

// Canonical visited-page key: pathname + sorted query string.
function normalizePageKey(urlString, baseOrigin) {
  const base = new URL(baseOrigin);
  const u = new URL(urlString, base);
  const entries = [...u.searchParams.entries()].sort((a, b) => {
    if (a[0] !== b[0]) return a[0].localeCompare(b[0]);
    return a[1].localeCompare(b[1]);
  });
  const search = new URLSearchParams();
  for (const [key, value] of entries) {
    search.append(key, value);
  }
  const qs = search.toString();
  return `${u.pathname}${qs ? `?${qs}` : ""}`;
}

const APPROVED_DEV_PORTS = new Set(["3000", "8000"]);

function defaultPortForProtocol(protocol) {
  return protocol === "https:" ? "443" : "80";
}

function normalizedPort(url) {
  return url.port || defaultPortForProtocol(url.protocol);
}

// Validate DRF next URL and return relative transport params.
// Rejects embedded credentials, protocol mismatch, unauthorized ports,
// foreign hosts, and non-exact Task-list pathnames.
function validateNextLink(rawNext, locationContext, visited) {
  if (!rawNext) return null;
  try {
    const base = new URL(locationContext.origin);
    const u = new URL(rawNext, base);

    if (u.protocol !== "http:" && u.protocol !== "https:") {
      return null;
    }

    if (u.username || u.password) {
      return null;
    }

    if (u.protocol !== locationContext.protocol) {
      return null;
    }

    const sameOrigin = u.origin === locationContext.origin;
    const sameHostname = u.hostname === locationContext.hostname;
    if (!sameOrigin && !sameHostname) {
      return null;
    }

    if (sameHostname) {
      const appPort = normalizedPort(new URL(locationContext.origin));
      const nextPort = normalizedPort(u);
      const allowedPorts = new Set([
        appPort,
        ...APPROVED_DEV_PORTS,
      ]);
      if (!allowedPorts.has(nextPort)) {
        return null;
      }
    }

    if (u.pathname !== TASK_LIST_PATH) {
      return null;
    }

    const pageKey = normalizePageKey(u.toString(), locationContext.origin);
    if (visited.has(pageKey)) {
      return null;
    }

    return {
      pageKey,
      params: paramsFromSearchParams(u.searchParams),
    };
  } catch {
    return null;
  }
}

/**
 * Fetch all pages of tasks for the given month.
 * Returns { ok: true, tasks: [...], pageCount: N } or
 *         { ok: false, error: message, limitExceeded: bool }
 */
export async function fetchAllCalendarTasks(params, { signal } = {}) {
  const locationContext = getLocationContext();

  const allTasks = [];
  const seenIds = new Set();
  const visited = new Set();

  let pageCount = 0;
  let nextParams = null;
  let isFirst = true;

  while (true) {
    if (signal && signal.aborted) {
      return { ok: false, aborted: true };
    }

    let payload;
    try {
      if (isFirst) {
        const res = await api.get(TASKS_API_RELATIVE, { params, signal });
        payload = res.data;
        isFirst = false;
      } else {
        const res = await api.get(TASKS_API_RELATIVE, { params: nextParams, signal });
        payload = res.data;
      }
    } catch (err) {
      if (signal && signal.aborted) return { ok: false, aborted: true };
      const message =
        (err && err.response && err.response.data && err.response.data.detail) ||
        (err && err.message) ||
        "Помилка завантаження";
      return { ok: false, error: message };
    }

    pageCount += 1;

    if (pageCount > MAX_PAGES) {
      return { ok: false, error: "Перевищено ліміт сторінок", limitExceeded: true };
    }

    const results = Array.isArray(payload.results) ? payload.results : [];
    for (const task of results) {
      if (task.id != null && !seenIds.has(task.id)) {
        seenIds.add(task.id);
        allTasks.push(task);
      }
    }

    const rawNext = payload.next || null;
    if (!rawNext) break;

    if (pageCount >= MAX_PAGES) {
      return {
        ok: false,
        error: "Перевищено ліміт сторінок",
        limitExceeded: true,
      };
    }

    const validated = validateNextLink(rawNext, locationContext, visited);
    if (!validated) {
      return {
        ok: false,
        error: "Небезпечне або повторне посилання на наступну сторінку",
        unsafeNext: true,
      };
    }

    visited.add(validated.pageKey);
    nextParams = validated.params;
  }

  return { ok: true, tasks: allTasks, pageCount };
}

export {
  TASK_LIST_PATH,
  TASKS_API_RELATIVE,
  MAX_PAGES,
  normalizePageKey,
  validateNextLink,
  getLocationContext,
};
